<template>
  <div class="card border-0 shadow-sm">
    <div class="card-header pb-0">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h6 class="mb-0">Seguimiento por Técnico</h6>
        <div class="d-flex align-items-center gap-2">
          <input
            v-model="search"
            type="text"
            class="form-control form-control-sm"
            style="max-width: 180px;"
            placeholder="Buscar técnico..."
            @input="onSearch"
          />
          <input
            v-model="selectedMonth"
            type="number"
            min="1"
            max="12"
            class="form-control form-control-sm"
            style="width: 70px;"
            placeholder="Mes"
            @change="resetAndLoad"
          />
          <input
            v-model="selectedYear"
            type="number"
            min="2020"
            max="2099"
            class="form-control form-control-sm"
            style="width: 90px;"
            placeholder="Año"
            @change="resetAndLoad"
          />
        </div>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      </div>
      <div v-else-if="data">
        <div class="row g-3 px-3 mb-3">
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Total Trabajos</p>
              <h5 class="font-weight-bolder mb-0">{{ data.totals.tasks_count }}</h5>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Total Ingresos</p>
              <h5 class="font-weight-bolder mb-0 text-success">{{ formatMoney(data.totals.total_revenue) }}</h5>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Total Pagado</p>
              <h5 class="font-weight-bolder mb-0">{{ formatMoney(data.totals.total_paid) }}</h5>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Pendiente</p>
              <h5 class="font-weight-bolder mb-0 text-warning">{{ formatMoney(data.totals.pending) }}</h5>
            </div>
          </div>
        </div>
        <div class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">Técnico</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Trabajos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ingresos</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pagado</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pendiente</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Var. Ingresos</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tech in data.technicians" :key="tech.user_id">
                <td class="text-sm ps-3 font-weight-bold">{{ tech.name }}</td>
                <td class="text-center text-sm">{{ tech.tasks_count }}
                  <span v-if="tech.tasks_variation_percent !== 0" class="text-xs ms-1" :class="tech.tasks_variation_percent >= 0 ? 'text-success' : 'text-danger'">
                    {{ tech.tasks_variation_percent >= 0 ? '↑' : '↓' }}{{ Math.abs(tech.tasks_variation_percent) }}%
                  </span>
                </td>
                <td class="text-center text-sm text-success font-weight-bold">{{ formatMoney(tech.total_revenue) }}</td>
                <td class="text-center text-sm">{{ formatMoney(tech.total_paid) }}</td>
                <td class="text-center text-sm text-warning">{{ formatMoney(tech.pending) }}</td>
                <td class="text-center text-sm">
                  <span :class="tech.revenue_variation_percent >= 0 ? 'text-success' : 'text-danger'">
                    {{ tech.revenue_variation_percent >= 0 ? '↑' : '↓' }} {{ Math.abs(tech.revenue_variation_percent) }}%
                  </span>
                </td>
              </tr>
              <tr v-if="data.technicians.length === 0">
                <td colspan="6" class="text-center text-sm text-secondary py-3">No hay datos para este periodo</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex align-items-center justify-content-between px-3 pt-2" v-if="data.pagination">
          <span class="text-xs text-secondary">
            {{ data.pagination.total }} registros · página {{ data.pagination.page }} de {{ data.pagination.last_page }}
          </span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-primary mb-0" :disabled="data.pagination.page <= 1" @click="changePage(data.pagination.page - 1)">Anterior</button>
            <button class="btn btn-outline-primary mb-0" :disabled="data.pagination.page >= data.pagination.last_page" @click="changePage(data.pagination.page + 1)">Siguiente</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TechnicianTracking',
  data() {
    const now = new Date();
    return {
      loading: false,
      data: null,
      selectedMonth: now.getMonth() + 1,
      selectedYear: now.getFullYear(),
      search: '',
      page: 1,
      searchTimer: null,
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch('metrics/getTechnicianTracking', {
          month: this.selectedMonth,
          year: this.selectedYear,
          search: this.search,
          page: this.page,
        });
        this.data = response.data;
      } catch (error) {
        console.error('Error loading technician tracking:', error);
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.loadData();
      }, 400);
    },
    resetAndLoad() {
      this.page = 1;
      this.loadData();
    },
    changePage(page) {
      this.page = page;
      this.loadData();
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>
